import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Member } from '../types';
import {
  Users,
  AlertTriangle,
  Zap,
  CheckCircle,
  TrendingUp,
  Activity,
  Bell,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { StatsCard } from '../components/ui/Card';
import { Badge, PriorityBadge } from '../components/ui/Badge';
import { Pill } from '../components/ui/Pill';

interface AgentActivity {
  id: string;
  activity_type: string;
  title: string;
  description: string;
  member_name: string | null;
  created_at: string;
}

interface DataSource {
  source_name: string;
  status: string;
  record_count: number;
  last_sync_at: string;
}

export default function DashboardEnhanced() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [agentActivities, setAgentActivities] = useState<AgentActivity[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'critical' | 'high' | 'medium'>('all');

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchAgentActivities, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [membersRes, activitiesRes, sourcesRes] = await Promise.all([
        supabase
          .from('members')
          .select('*')
          .order('risk_score', { ascending: false })
          .limit(50),
        supabase
          .from('agent_activities')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(10),
        supabase.from('data_sources').select('*'),
      ]);

      if (membersRes.data) setMembers(membersRes.data);
      if (activitiesRes.data) setAgentActivities(activitiesRes.data);
      if (sourcesRes.data) setDataSources(sourcesRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentActivities = async () => {
    try {
      const { data } = await supabase
        .from('agent_activities')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (data) setAgentActivities(data);
    } catch (error) {
      console.error('Error fetching agent activities:', error);
    }
  };

  const getRiskPriority = (score: number): 'high' | 'medium' | 'low' | 'critical' => {
    if (score >= 4.5) return 'critical';
    if (score >= 4) return 'high';
    if (score >= 3) return 'medium';
    return 'low';
  };

  const getRiskBand = (score: number): string => {
    if (score >= 4.5) return 'Critical';
    if (score >= 4) return 'High';
    if (score >= 3) return 'Medium';
    return 'Low';
  };

  const filteredMembers = members.filter((member) => {
    if (priorityFilter === 'all') return true;
    const priority = getRiskPriority(member.risk_score);
    return priority === priorityFilter;
  });

  const stats = {
    activeCases: members.length,
    risingRiskAlerts: members.filter((m) => m.risk_score >= 4).length,
    agentActions: 892,
    connectedSources: dataSources.filter((ds) => ds.status === 'connected').length,
    totalSources: dataSources.length,
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'risk_update':
        return <TrendingUp className="w-4 h-4" />;
      case 'alert':
        return <AlertTriangle className="w-4 h-4" />;
      case 'data_sync':
        return <RefreshCw className="w-4 h-4" />;
      case 'flag_added':
        return <Bell className="w-4 h-4" />;
      case 'intervention':
        return <Zap className="w-4 h-4" />;
      case 'assessment':
        return <CheckCircle className="w-4 h-4" />;
      case 'care_plan':
        return <Activity className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'risk_update':
        return 'text-orange-600';
      case 'alert':
        return 'text-red-600';
      case 'data_sync':
        return 'text-blue-600';
      case 'flag_added':
        return 'text-purple-600';
      case 'intervention':
        return 'text-green-600';
      case 'assessment':
        return 'text-purple-600';
      case 'care_plan':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-6">
        <StatsCard
          title="Active Cases"
          value={stats.activeCases.toLocaleString()}
          icon={<Users className="w-6 h-6" />}
          trend="↑ 12% from last week"
          variant="default"
        />

        <StatsCard
          title="Rising Risk Alerts"
          value={stats.risingRiskAlerts}
          icon={<AlertTriangle className="w-6 h-6" />}
          variant="danger"
          action={{
            label: 'Click to review →',
            onClick: () => { },
          }}
        />

        <StatsCard
          title="Agent Actions"
          value={stats.agentActions}
          icon={<Zap className="w-6 h-6" />}
          variant="primary"
          action={{
            label: 'View activity log →',
            onClick: () => { },
          }}
        />

        <StatsCard
          title="Data Sources"
          value={`${stats.connectedSources}/${stats.totalSources}`}
          icon={<CheckCircle className="w-6 h-6" />}
          trend="All systems synced"
          variant="success"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Priority Member Queue */}
        <div className="col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Priority Member Queue</h2>
          </div>

          {/* Filter Tabs */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex gap-2">
            {[
              { id: 'all', label: 'All' },
              { id: 'critical', label: 'Critical' },
              { id: 'high', label: 'High' },
              { id: 'medium', label: 'Medium' },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setPriorityFilter(filter.id as any)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${priorityFilter === filter.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Member List */}
          <div className="divide-y divide-gray-200">
            {filteredMembers.slice(0, 10).map((member) => (
              <div
                key={member.id}
                onClick={() => navigate(`/members/${member.member_id}`)}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {member.first_name} {member.last_name}
                        </h3>
                        <PriorityBadge priority={getRiskPriority(member.risk_score)} />
                        {member.risk_score >= 4 && <TrendingUp className="w-4 h-4 text-red-500" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {member.member_id} • {new Date().getFullYear() - new Date(member.date_of_birth).getFullYear()}yo • {member.program || 'Medicaid-TX'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Pill variant="primary" className="text-xs">Diabetes Type 2</Pill>
                        <Pill variant="primary" className="text-xs">CHF</Pill>
                        <Pill variant="primary" className="text-xs">CKD Stage 3</Pill>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 ml-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{member.risk_score.toFixed(1)}</p>
                      <p className="text-xs text-gray-500">Risk</p>
                      <p className="text-xs text-gray-600 mt-1">{getRiskBand(member.risk_score)}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Agent Activity Feed */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Agent Activity</h2>
            <button
              onClick={fetchAgentActivities}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
            {agentActivities.map((activity) => (
              <div key={activity.id} className="px-6 py-4">
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-lg ${getActivityColor(activity.activity_type)}`}>
                    {getActivityIcon(activity.activity_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    {activity.member_name && (
                      <p className="text-sm text-gray-900 mt-0.5">{activity.member_name}</p>
                    )}
                    {activity.description && (
                      <p className="text-xs text-gray-600 mt-1">{activity.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">{formatTimeAgo(activity.created_at)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div> */}

          <div className="p-4 space-y-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Risk score updated</p>
                <p className="text-xs text-gray-500">Maria Santos</p>
                <p className="text-xs text-gray-400 mt-1">
                  Score increased from 3.8 to 4.2 based on new claims data
                </p>
                <p className="text-xs text-purple-600 mt-1">12:47 PM</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">New discharge alert</p>
                <p className="text-xs text-gray-500">Dorothy Chen</p>
                <p className="text-xs text-gray-400 mt-1">
                  Hospital discharge detected - TOC protocol triggered
                </p>
                <p className="text-xs text-purple-600 mt-1">12:45 PM</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Data sync completed</p>
                <p className="text-xs text-gray-500">System</p>
                <p className="text-xs text-gray-400 mt-1">
                  Claims feed refreshed - 1,247 new records processed
                </p>
                <p className="text-xs text-purple-600 mt-1">12:42 PM</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">SDOH flag added</p>
                <p className="text-xs text-gray-500">Maria Santos</p>
                <p className="text-xs text-gray-400 mt-1">
                  Transportation barrier identified from community resource referral
                </p>
                <p className="text-xs text-purple-600 mt-1">12:38 PM</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Assessment due alert</p>
                <p className="text-xs text-gray-500">James Wilson</p>
                <p className="text-xs text-gray-400 mt-1">
                  Annual reassessment due in 14 days
                </p>
                <p className="text-xs text-purple-600 mt-1">12:35 PM</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Pathway recommendation
                </p>
                <p className="text-xs text-gray-500">Robert Thompson</p>
                <p className="text-xs text-gray-400 mt-1">
                  Downgraded to Wellness track based on improving trend
                </p>
                <p className="text-xs text-purple-600 mt-1">12:30 PM</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
